import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  authService,
  plantService,
  userService,
  machineService,
  masterDataService,
  areaService,
} from "../services";
import { ONBOARDING_STEPS } from "../navigation/onboardingSteps";

function buildPaletteIdSet(paletteEntries) {
  return new Set((paletteEntries || []).map((e) => e.id).filter(Boolean));
}

/**
 * Loads master counts and returns which onboarding checks pass.
 */
async function loadCheckResults(tenantId) {
  const [
    plants,
    shifts,
    users,
    areas,
    departments,
    machines,
    lossTypes,
  ] = await Promise.all([
    plantService.getAllPlants().catch(() => []),
    masterDataService.getShifts(tenantId).catch(() => []),
    userService.getAllUsers().catch(() => []),
    areaService.getAllAreas().catch(() => []),
    masterDataService.getDepartments(tenantId).catch(() => []),
    machineService.getAllMachines().catch(() => []),
    masterDataService.getLossTypes(tenantId).catch(() => []),
  ]);

  const userList = users || [];
  return {
    plants: (plants || []).length > 0,
    shifts: (shifts || []).length > 0,
    users: userList.length >= 2,
    areas: (areas || []).length > 0,
    departments: (departments || []).length > 0,
    machines: (machines || []).length > 0,
    lossTypes: (lossTypes || []).length > 0,
  };
}

/**
 * @param {object} opts
 * @param {Array<{ id: string, path: string, label: string }>} opts.paletteEntries
 * @param {boolean} opts.featuresLoading
 */
export function useTenantOnboardingStatus({ paletteEntries, featuresLoading }) {
  /** True only during the first load when there are no steps yet (avoids flicker on refetch). */
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const applicableDefs = useMemo(() => {
    if (featuresLoading) return [];
    const ids = buildPaletteIdSet(paletteEntries);
    return ONBOARDING_STEPS.filter((s) => ids.has(s.navKey)).sort(
      (a, b) => a.order - b.order,
    );
  }, [paletteEntries, featuresLoading]);

  const applicableDefsRef = useRef(applicableDefs);
  applicableDefsRef.current = applicableDefs;
  const paletteEntriesRef = useRef(paletteEntries);
  paletteEntriesRef.current = paletteEntries;

  const applicableDefsSig = useMemo(
    () => applicableDefs.map((d) => d.id).join("\0"),
    [applicableDefs],
  );

  const refetch = useCallback(async () => {
    const defs = applicableDefsRef.current;
    const pe = paletteEntriesRef.current;
    const tenantId = authService.getTenantId();
    if (!tenantId || defs.length === 0) {
      setSteps([]);
      setBootstrapLoading(false);
      return;
    }
    const silent = stepsRef.current.length > 0;
    if (!silent) {
      setBootstrapLoading(true);
    }
    try {
      const results = await loadCheckResults(tenantId);

      const paletteById = new Map((pe || []).map((e) => [e.id, e]));

      const built = defs.map((def) => {
        const done = results[def.checkKey] === true;
        const entry = paletteById.get(def.navKey);
        return {
          ...def,
          done,
          path: entry?.path || "/home",
          label: entry?.label || def.title,
        };
      });

      setSteps(built);
    } catch (e) {
      console.error("Onboarding status load failed:", e);
      setSteps([]);
    } finally {
      setBootstrapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (featuresLoading) return;
    refetch();
  }, [featuresLoading, applicableDefsSig, refetch]);

  return useMemo(() => {
    const completedCount = steps.filter((s) => s.done).length;
    const totalCount = steps.length;
    const allDone = totalCount > 0 && completedCount === totalCount;
    const percentComplete =
      totalCount === 0 ? 100 : Math.round((completedCount / totalCount) * 100);
    return {
      /** For UI: first-time guide load only; refetches do not set this. */
      guideLoading: featuresLoading || bootstrapLoading,
      steps,
      applicableDefs,
      completedCount,
      totalCount,
      allDone,
      percentComplete,
      refetch,
    };
  }, [
    featuresLoading,
    bootstrapLoading,
    steps,
    applicableDefs,
    refetch,
  ]);
}
